import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import styles from './referrals-form.module.scss';

import { useTranslation } from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';

import Button from './button';

import {
  gotError,
  recaptchaVerified,
  sendReferralRequest,
} from '../../redux/features/referrals/referralsSlice';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { emailPattern } from '../util';
import Select from './select';
import Message from './message';

interface IFormInputs {
  email: string;
  orderNumber: string;
  recipientEmail: string;
  snooOption: string;
}

const isRenter = (value: string): boolean | null => {
  return !value ? null : value === 'RENT_SNOO';
};

const ReferralsForm = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { error, success, receivedCustomerType, recaptchaPassed, showLoader } =
    useAppSelector((state) => {
      return {
        error: state.referrals.error,
        success: state.referrals.success,
        receivedCustomerType: state.referrals.receivedCustomerType,
        recaptchaPassed: state.referrals.recaptchaPassed,
        showLoader: state.referrals.showLoader,
      };
    });

  const [snooOption, setSnooOption] = useState({
    key: '',
    code: '',
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormInputs>();
  const onSubmit: SubmitHandler<IFormInputs> = (data) => {
    // TODO: remove me once recaptcha token is generated on EU region
    if (process.env.REACT_APP_REGION === 'us') {
      if (!recaptchaPassed) {
        dispatch(gotError('Please verify reCAPTCHA'));
        return;
      }
    }
    dispatch(
      sendReferralRequest({
        isRenter: isRenter(snooOption.code),
        userEmail: data.email,
        orderNumber: data.orderNumber,
        referredEmail: data.recipientEmail,
      })
    );
  };
  useEffect(() => {
    if (recaptchaPassed) {
      dispatch(gotError(''));
    }
  }, [dispatch, recaptchaPassed]);
  return (
    <div>
      <div className='my-3'>
        <p className='form-field-title'>
          <span className='text-danger'>*</span>
          {t('rent_or_own_snoo', 'Do you rent or own SNOO')}
        </p>
        <Select
          onChange={(name, index, item) => {
            setSnooOption(item);
          }}
          disabled={false}
          list={[
            { key: 'I purchased SNOO', code: 'OWN_SNOO' },
            { key: 'I rented SNOO', code: 'RENT_SNOO' },
          ]}
          placeholder='Select option'
          name='snoo_option'
          listOpen={true}
          headerTitle={snooOption.key}
          defaultValue={0}
        />
      </div>
      <form noValidate onSubmit={handleSubmit(onSubmit)} className='mt-3'>
        <p
          className={`form-field-title mb-3 ${
            snooOption.code ? 'form-field-title' : 'text-disabled'
          }`}
        >
          <span
            className={`${snooOption.code ? 'text-danger' : 'text-disabled'}`}
          >
            *
          </span>
          {t('personal_information', 'Personal Information')}
        </p>
        <div className='mb-2 form-floating custom-input'>
          <input
            type='email'
            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
            id='email'
            placeholder='name@example.com'
            disabled={!snooOption.code}
            aria-invalid={errors.email ? 'true' : 'false'}
            {...register('email', {
              required: {
                value: true,
                message: t('email_input_error_required', 'Email is required'),
              },
              pattern: {
                value: emailPattern,
                message: t(
                  'email_input_error_pattern',
                  'You have entered an invalid e-mail address. Please enter a valid email.'
                ),
              },
            })}
          />
          <label
            htmlFor='email'
            className={`font-size-sm ${!snooOption.code ? 'disabled' : ''}`}
          >
            {t('email_input_label', 'Your email')}
          </label>
          {errors.email && (
            <p className='invalid-feedback d-block'>{errors.email.message}</p>
          )}
        </div>
        <p className={`text-helper ${!snooOption.code ? 'text-disabled' : ''}`}>
          *{' '}
          {t(
            'email_helper',
            'Please enter the email that was used in the process of buying or renting the SNOO'
          )}
        </p>
        <div className='mb-3 form-floating custom-input'>
          <input
            className={`form-control ${errors.orderNumber ? 'is-invalid' : ''}`}
            type='text'
            disabled={!snooOption.code}
            placeholder={t('order_number', 'Order number')}
            aria-label='input order number'
            {...register('orderNumber', {
              required: {
                value: true,
                message: t(
                  'order_number_error_required',
                  'Order number is required.'
                ),
              },
              minLength: {
                value: 4,
                message: t(
                  'invalid_order_number_entered',
                  'You have entered an invalid order number. Please check your input and try again.'
                ),
              },
            })}
          />
          <label
            htmlFor='orderNumber'
            className={`font-size-sm ${!snooOption.code ? 'disabled' : ''}`}
          >
            {t('order_number_label', 'Order Number')}
          </label>
          {errors.orderNumber && (
            <p className='invalid-feedback d-block'>
              {errors.orderNumber.message}
            </p>
          )}
        </div>
        <p
          className={`form-field-title mb-3 ${
            snooOption.code ? 'form-field-title' : 'text-disabled'
          }`}
        >
          <span
            className={`${snooOption.code ? 'text-danger' : 'text-disabled'}`}
          >
            *
          </span>
          {t('referral_recipient_info', 'Referral recipient information')}
        </p>
        <div className='mb-3 form-floating custom-input'>
          <input
            type='email'
            className={`form-control ${
              errors.recipientEmail ? 'is-invalid' : ''
            }`}
            id='recipientEmail'
            placeholder='name@example.com'
            disabled={!snooOption.code}
            aria-invalid={errors.recipientEmail ? 'true' : 'false'}
            {...register('recipientEmail', {
              required: {
                value: true,
                message: t(
                  'recipient_email_input_error_required',
                  'Recipient e-mail is required'
                ),
              },
              pattern: {
                value: emailPattern,
                message: t('email_input_error_pattern'),
              },
            })}
          />
          <label
            htmlFor='recipientEmail'
            className={`font-size-sm ${!snooOption.code ? 'disabled' : ''}`}
          >
            {t('email_input_label_recipient', 'Referral recipient email')}
          </label>
          {errors.recipientEmail && (
            <p className='invalid-feedback d-block'>
              {errors.recipientEmail.message}
            </p>
          )}
        </div>

        <div
          className={
            styles.Recaptcha + ' d-flex container justify-content-center'
          }
        >
          <ReCAPTCHA
            sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY || ''}
            onChange={(v: string | null) => dispatch(recaptchaVerified(v))}
          />
        </div>
        <div className={'d-flex container justify-content-center'}>
          {!!error && (
            <Message type='error-light' fullWidth={true}>
              {error}
            </Message>
          )}
        </div>
        <div className={'d-flex container justify-content-center'}>
          {success && (
            <Message type='success-light' fullWidth={true}>
              {receivedCustomerType === 'rental20'
                ? t(
                    'referral_sent_success_with_login_info',
                    'Your referral was sent! Just so you know, you can log in to your rental portal to send and track referrals too.'
                  )
                : t('referral_sent_success', 'Your referral was sent!')}
            </Message>
          )}
        </div>
        <div className='button-wrapper d-flex container justify-content-center'>
          <Button
            disabled={showLoader}
            color={'red'}
            onClick={() => {}}
            text={t('btn_submit', 'Submit referral')}
            name='submit'
            showSpinner={showLoader}
          />
        </div>
      </form>
    </div>
  );
};

export default ReferralsForm;
